import dayjs, { Dayjs } from 'dayjs';
import {
  action,
  computed,
  IObservableArray,
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import {
  CalendarParamsReq,
  CalendarResponse,
} from '../../api/api-types/calendar';
import { AssetsApi } from '../../api/assets';
import { ApiReq, emptyValue } from '../../api';
import { AssetCreationType, AssetItem } from '../../api/api-types/assets';
import { TablePagination } from '../pagination/pagination';
import { CalendarItem } from './calendar-item';
import { CalendarApi } from '../../api/calendar';
import { AssetModification } from '../assets/asset-modification';
import { lookupsStore } from '../../stores/lookups';
import { removeTimezone } from '../../utils/date';

export interface InitDataCalendarType {
  initialDate?: Date | Dayjs | null;
}

export enum AllOrMyType {
  ALL = 1,
  MY = 2,
}

export enum ActivitiesListsType {
  Activities = 1,
  AssignedActivities = 2,
}

export class CalendarModel {
  readonly api = new AssetsApi();
  readonly calendarApi = new CalendarApi();

  @observable selectedDate: Date | Dayjs | null;
  @observable calendarDate: Date | Dayjs;
  @observable allOrMy = AllOrMyType.MY;
  @observable selectedActivity: CalendarItem | null = null;
  @observable editingActivity: AssetModification | null = null;
  @observable showDisplayDetails: boolean = false;
  @observable showDisplayEditAsset: boolean = false;

  @observable.ref calendarActivities: IObservableArray<CalendarItem> =
    observable([]);
  @observable.ref calendarReq: ApiReq<CalendarResponse> = emptyValue;
  @observable.ref activityEntityReq: ApiReq<AssetItem> = emptyValue;

  @observable pagination = new TablePagination<CalendarResponse>({
    requestRef: this.calendarReq,
  });

  constructor(initialDate?: InitDataCalendarType['initialDate']) {
    makeAutoObservable(this);
    this.selectedDate = null;
    this.calendarDate = initialDate ? initialDate : new Date();
    this.pagination.setFetchingCallBack(this.fetchCalendarActivities);

    reaction(
      () => [this.allOrMy],
      () => this.getCalendarActivities(),
    );

    reaction(
      () => [this.startOf, this.endOf],
      () => this.getCalendarActivities(),
    );

    reaction(
      () => [this.selectedActivity],
      () => {
        if (this.selectedActivity) {
          return this.fetchActivity(this.selectedActivity?.id);
        }
      },
    );
  }

  @computed get calendarData() {
    if (this.calendarReq.state !== 'fulfilled' || !this.calendarReq.value) {
      return [];
    }

    return this.calendarReq.value.data?.items || [];
  }

  @computed get isEditable() {
    if (!this.selectedActivityData) return false;

    return (
      this.selectedActivityData.canUpdate &&
      this.selectedActivityData.isRelatedEntityActive
    );
  }

  @computed get selectedActivityData() {
    if (
      this.activityEntityReq.state !== 'fulfilled' ||
      !this.activityEntityReq.value
    ) {
      return null;
    }

    return this.activityEntityReq.value.data || null;
  }

  @computed get startOf(): string {
    return this.calendarDate
      ? format(
          startOfWeek(
            startOfMonth(
              this.calendarDate instanceof Date
                ? this.calendarDate
                : this.calendarDate.toDate(),
            ),
            { weekStartsOn: 0 },
          ),
          'yyyy-MM-dd',
        )
      : format(
          new Date(startOfWeek(startOfMonth(new Date()), { weekStartsOn: 0 })),
          'yyyy-MM-dd',
        );
  }

  @computed get endOf(): string {
    return this.calendarDate
      ? format(
          endOfWeek(
            endOfMonth(
              this.calendarDate instanceof Date
                ? this.calendarDate
                : this.calendarDate.toDate(),
            ),
            { weekStartsOn: 0 },
          ),
          'yyyy-MM-dd',
        )
      : format(
          new Date(endOfWeek(endOfMonth(new Date()), { weekStartsOn: 0 })),
          'yyyy-MM-dd',
        );
  }

  @action switchTargetScope = (value: AllOrMyType) => {
    this.allOrMy = value;
  };

  @action toggleDetails = (force?: boolean) => {
    this.showDisplayDetails =
      force !== undefined ? force : !this.showDisplayDetails;
  };
  @action toggleEditAsset = (force?: boolean) => {
    this.showDisplayEditAsset =
      force !== undefined ? force : !this.showDisplayEditAsset;
  };

  @action selectItem = (item: CalendarItem | null) => {
    this.selectedActivity = item;
  };

  @action pickItem = (item: CalendarItem | null) => {
    this.selectItem(item);
    this.toggleDetails(true);
  };

  @action editItem = () => {
    if (!this.selectedActivity)
      // eslint-disable-next-line no-console
      return console.warn('No Activity selected');

    this.toggleDetails(false);
    (async () => {
      if (!this.selectedActivityData)
        // eslint-disable-next-line no-console
        return console.warn('No Activity selected');

      //todo need code improvement for better reading
      this.editingActivity = new AssetModification({
        ...this.selectedActivityData,
        assetDate: this.selectedActivityData.assetDate
          ? removeTimezone(this.selectedActivityData.assetDate)
          : this.selectedActivityData.assetDate,
        dueDate: this.selectedActivityData.dueDate
          ? removeTimezone(this.selectedActivityData.dueDate)
          : this.selectedActivityData.assetDate,
        dateIssued: removeTimezone(this.selectedActivityData.dateIssued),
        waiverInfoStatusId: this.selectedActivityData.waiverInfo
          ? this.selectedActivityData.waiverInfo.status.id
          : undefined,
        waiverInfoFollowUpDate: this.selectedActivityData.waiverInfo
          ?.followUpDate
          ? removeTimezone(this.selectedActivityData.waiverInfo.followUpDate)
          : undefined,
      });
      return this.editingActivity;
    })().then(activeEntity => {
      this.initLookups(activeEntity as AssetModification).then(() =>
        this.toggleEditAsset(true),
      );
    });
  };

  @action initLookups = async (activity: AssetModification) =>
    Promise.allSettled([
      activity.getFullDescription(),
      lookupsStore?.initActivityCallsForEditing({
        activityTypeId: activity.type?.id!,
        activityCategoryId: activity.category?.id,
        activityRelatedToTypeId: activity.relatedToType?.id,
        activityRelatedTo: activity.relatedTo?.id,
      }),
    ]);

  @action unpickItem = () => {
    this.selectItem(null);
    this.toggleDetails(false);
  };

  @action setCalendarDate = (newDate: Date | Dayjs) => {
    runInAction(() => {
      this.calendarDate = dayjs(newDate).toDate();
    });
  };

  @action selectDate = (newDate: Date | Dayjs | null) => {
    runInAction(() => {
      this.selectedDate = newDate ? dayjs(newDate).toDate() : null;
    });
  };

  @action fetchActivity = async (id: number) => {
    this.activityEntityReq = this.api.getActivityById(id);

    await this.activityEntityReq;

    return this.activityEntityReq;
  };

  @action getCalendarActivities = () => {
    return this.fetchCalendarActivities({
      'q.Date.From': this.startOf,
      'q.Date.To': this.endOf,
      'q.mode': this.allOrMy,
    });
  };

  @action cancelEditing = () => {
    this.resetEditingActivity();
  };

  @action fetchCalendarActivities = async (args: CalendarParamsReq) => {
    this.calendarReq = this.calendarApi.calendarActivities(args);

    await this.calendarReq;
    runInAction(() => {
      if (
        this.calendarReq.state !== 'fulfilled' ||
        this.calendarReq.value?.data === null
      )
        return;
      this.calendarActivities.replace(
        this.calendarData.map(data => new CalendarItem(data)),
      );
      this.pagination.setPagination(this.calendarReq.value?.data);
    });
    return this.calendarReq;
  };

  @action deleteActivity = async () => {
    await this.editingActivity?.deleteAsset();
    await this.fetchCalendarActivities({
      'q.Date.From': this.startOf,
      'q.Date.To': this.endOf,
      'q.mode': this.allOrMy,
    });

    this.resetEditingActivity();
  };

  @action updateEditActivity = async (data: any) => {
    await this.editingActivity?.setEntity({
      ...(data as unknown as Partial<AssetCreationType>),
      updateDescription: true,
    });
    await this.editingActivity?.editAsset();
    await this.fetchCalendarActivities({
      'q.Date.From': this.startOf,
      'q.Date.To': this.endOf,
      'q.mode': this.allOrMy,
    });
    this.resetEditingActivity();
  };

  @action resetEditingActivity = () => {
    this.selectedActivity = null;
    this.editingActivity = null;
    this.showDisplayDetails = false;
    this.showDisplayEditAsset = false;
  };

  @action resetStore = () => {
    this.allOrMy = AllOrMyType.MY;
    this.setCalendarDate(new Date());
    this.resetEditingActivity();
  };
}
